// PortfolioV2 — clean, editorial home page in the same aesthetic as /latest.
// Inter Light + Lora serif headlines, stone palette, dot-grid background,
// generous whitespace, subtle motion. Replaces the older Snapshot-driven home.

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  IconMcpJest,
  IconAaKit,
  IconEngram,
  IconApproval,
  IconFreeport,
  IconGrain,
  IconPixelpi,
  IconTongue,
  IconLockbox,
  IconHealthShield,
} from "./ProjectIcons";
import {
  FileText,
  Github,
  Mail,
  BookOpen,
  ArrowUpRight,
  Sparkles,
  Linkedin,
} from "lucide-react";

// ──────────────────────────────── data ────────────────────────────────

const EXPERIENCE: Array<{
  year: string;
  company: string;
  url?: string;
  logo?: string;
  role: string;
  location: string;
  note: string;
}> = [
  {
    year: "2026-now",
    company: "Coupang",
    logo: "/logos/coupang.png",
    role: "Senior Software Engineer",
    location: "Bengaluru",
    note: "Backend and platform work for one of Asia's largest e-commerce operations: a few million orders a day, tens of millions of users, and almost everything delivered within 24 hours.",
  },
  {
    year: "2025",
    company: "Early-stage AI startup",
    role: "Founding team, forward-deployed engineer",
    location: "Remote",
    note: "Six months trying out an idea with a co-founder, building an \"AI operating partner\" for regulated enterprises. Embedded with Cleartrip, Bharatsure, 100ms, Putting Scene. Learnt a lot; stepped back to do more focused work.",
  },
  {
    year: "2024-2026",
    company: "Visa (Authorize.Net)",
    logo: "/logos/visa.png",
    role: "Senior Software Engineer (L5)",
    location: "Bengaluru",
    note: "Worked on the gateway (500+ TPS, 445K merchants, around $200B a year) and ran some 0-to-1 applied-AI experiments inside it: MCP tooling, dev-productivity agents, QA automation, churn analysis.",
  },
  {
    year: "side",
    company: "Qoala",
    url: "https://qoala.app",
    logo: "/logos/qoala.png",
    role: "GenAI consultant",
    location: "SE Asia · Remote",
    note: "Helped a Southeast-Asian insurtech (50K agents, ~1M users) ship a GenAI motor-inspection platform doing around 10,000 inspections a month, plus their first AutoGen agent for cross-sell.",
  },
  {
    year: "2021-2024",
    company: "Plum",
    url: "https://plumhq.com",
    logo: "/logos/plum.png",
    role: "Founding engineer to Sr. SDE-III",
    location: "Bengaluru",
    note: "Founding engineer. Built greenfield systems for network hospitals, the mobile apps (Android and iOS on a KMM stack), retail term-life insurance (purchase journeys through to claims), and health benefits, and contributed to shared systems like claims, telehealth, and policies. The platform now backs around 6,000 companies and 600,000 people.",
  },
];

type Icon = (props: { className?: string }) => JSX.Element;

const RA_PROJECTS: Array<{ name: string; url: string; desc: string; icon: Icon }> = [
  {
    name: "MCP-JEST",
    url: "https://www.npmjs.com/package/mcp-jest",
    desc: "First eval/test framework for MCP servers.",
    icon: IconMcpJest,
  },
  {
    name: "aa-kit",
    url: "https://pypi.org/project/aa-kit/",
    desc: "MCP-native Python agent framework.",
    icon: IconAaKit,
  },
  {
    name: "Engram",
    url: "https://github.com/reallyartificial/engram",
    desc: "Agent memory: retain → recall → reflect.",
    icon: IconEngram,
  },
  {
    name: "Approval Protocol",
    url: "https://github.com/reallyartificial/approvalprotocol",
    desc: "Human-in-the-loop framework for agents.",
    icon: IconApproval,
  },
  {
    name: "Freeport",
    url: "https://github.com/reallyartificial/freeport",
    desc: "Open-source multi-provider LLM gateway.",
    icon: IconFreeport,
  },
  {
    name: "Grain",
    url: "https://grain.reallyartificial.org",
    desc: "Structured agent-definition format.",
    icon: IconGrain,
  },
];

const APPS: Array<{ name: string; url: string; meta: string; icon: Icon }> = [
  {
    name: "Tongue",
    url: "https://apps.apple.com/us/app/tongueapp/id6759001870",
    meta: "iOS · SwiftUI · a voice-AI language tutor",
    icon: IconTongue,
  },
  {
    name: "Lockbox",
    url: "https://apps.apple.com/us/app/lockboxapp/id6747517926",
    meta: "iOS · SwiftUI · a small credentials manager",
    icon: IconLockbox,
  },
  {
    name: "Plum Health Benefits",
    url: "https://apps.apple.com/in/app/plum-health-benefits/id1616851078",
    meta: "iOS · I shipped v1 as the only engineer",
    icon: IconHealthShield,
  },
];

const HEROES: Array<{ name: string; url: string; tag: string; desc: string; icon: Icon }> = [
  {
    name: "mcp-jest",
    url: "https://www.npmjs.com/package/mcp-jest",
    tag: "9K+ downloads",
    desc: "The first testing and evaluation framework for MCP servers. Like Jest, but for MCP: it asserts agent tool-calling correctness in CI.",
    icon: IconMcpJest,
  },
  {
    name: "pixelpi",
    url: "https://www.npmjs.com/package/pixelpi",
    tag: "new",
    desc: "A minimal browser-agent CLI and harness over the raw Chrome DevTools Protocol, so agents can drive real web apps.",
    icon: IconPixelpi,
  },
];

const PRODUCTS: Array<{ name: string; url: string; desc: string }> = [
  { name: "Friday", url: "/friday", desc: "An autonomous AI assistant with its own GitHub identity (@fridayjoshi)." },
  { name: "xeli", url: "https://xeli.josharsh.com", desc: "A terminal spreadsheet. Query CSV, JSON, and Parquet in plain English, with DuckDB under the hood." },
  { name: "otterly", url: "https://github.com/josharsh/otterly", desc: "Use your Claude Code subscription as a local, OpenAI-compatible API. Like Ollama, but for Claude." },
  { name: "InFlash", url: "https://inflash.vercel.app/", desc: "A macOS app for capturing thoughts by voice. On-device speech-to-text that types into any app or saves to Notes and Reminders." },
  { name: "Sketchflow", url: "https://sketchflow.josharsh.com", desc: "AI-powered sketching and diagramming on an infinite canvas." },
  { name: "Deadly Code Videos", url: "https://www.deadlycodevideos.com", desc: "Turn code into clean typing-animation videos." },
  { name: "Claude CTO", url: "https://claudecto.josharsh.com", desc: "A meta-AI for managing Claude Code." },
  { name: "The AI Terminal Course", url: "https://www.aiterminalcourse.com/", desc: "14 short modules on terminal-native AI." },
  { name: "Paper", url: "https://github.com/josharsh/paper", desc: "A Flutter document scanner with real-time edge detection." },
  { name: "ezff", url: "https://www.npmjs.com/package/ezff", desc: "Plain English wrapper around ffmpeg, so I don't have to keep Googling flags." },
];

const FEATURED_POSTS: Array<{ title: string; url: string; blurb: string }> = [
  {
    title: "Etiquette of a good agent!",
    url: "https://unengineered.substack.com/p/etiquettes-of-a-good-agent",
    blurb: "AI agents should operate within bounded autonomy, not unrestricted capability.",
  },
  {
    title: "Etiquette of a Good Software!",
    url: "https://unengineered.substack.com/p/etiquettes-of-a-good-software",
    blurb: "Well-designed code demonstrates considerate behavior toward future developers.",
  },
  {
    title: "I played with GPT OSS",
    url: "https://unengineered.substack.com/p/i-played-with-gpt-oss",
    blurb: "Hands-on exploration of OpenAI's open-source models and their capabilities.",
  },
];

// ──────────────────────────────── shared bits ────────────────────────────────

function SectionLabel({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <h2
      id={id}
      className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-500"
    >
      {children}
    </h2>
  );
}

function Divider() {
  return <hr className="my-12 border-t border-stone-200 md:my-16" />;
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-stone-900 underline decoration-stone-300 underline-offset-[3px] transition hover:decoration-stone-900"
    >
      {children}
      <ArrowUpRight className="h-3 w-3 opacity-50" />
    </a>
  );
}

// ──────────────────────────────── page ────────────────────────────────

export default function PortfolioV2() {
  return (
    <div className="min-h-screen bg-[#fafaf7] text-stone-900 font-inter selection:bg-stone-900 selection:text-stone-50">
      {/* faint dot background, matching /latest */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.025]"
        style={{
          backgroundImage:
            "radial-gradient(circle, #000 0.6px, transparent 0.6px)",
          backgroundSize: "18px 18px",
        }}
      />

      <div className="relative mx-auto max-w-3xl px-6 pb-32 pt-8 md:pt-14">
        {/* ── top bar ───────────────────────────────────────────── */}
        <header className="flex items-baseline justify-between gap-4 pb-10">
          <div>
            <Link
              to="/"
              className="text-[13px] font-semibold uppercase tracking-[0.18em] text-stone-900 transition hover:text-stone-600"
            >
              Harsh Joshi
            </Link>
            <p className="mt-1 text-[12px] text-stone-500">
              Engineering & dark arts
            </p>
          </div>
          <nav className="hidden gap-5 text-[12px] text-stone-500 sm:flex">
            <a href="/resume-openai-startups.pdf" target="_blank" className="inline-flex items-center gap-1 hover:text-stone-900">
              <FileText className="h-3.5 w-3.5" /> Resume
            </a>
            <a href="https://github.com/josharsh" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-stone-900">
              <Github className="h-3.5 w-3.5" /> GitHub
            </a>
            <a href="https://unengineered.substack.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-stone-900">
              <BookOpen className="h-3.5 w-3.5" /> Writing
            </a>
            <a href="mailto:harsh.joshi.pth@gmail.com" className="inline-flex items-center gap-1 hover:text-stone-900">
              <Mail className="h-3.5 w-3.5" /> Email
            </a>
          </nav>
        </header>

        {/* ── hero ──────────────────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="font-lora text-[40px] leading-[1.12] tracking-tight text-stone-900 md:text-[52px]">
            Hi, I'm Harsh.
          </h1>
          <p className="font-lora mt-5 max-w-xl text-[19px] leading-relaxed italic text-stone-700">
            I like building small things that work and big things that scale. Sometimes they're the same thing.
          </p>
          <p className="mt-4 max-w-xl text-[15.5px] leading-relaxed text-stone-600">
            6+ years on application backends, distributed systems, UI platforms, and applied AI, building in the open since Google Summer of Code. Currently at <span className="text-stone-900">Coupang</span>. Previously at Visa and{" "}
            <a
              href="https://plumhq.com"
              target="_blank"
              rel="noreferrer"
              className="text-stone-900 underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-900"
            >
              Plum
            </a>{" "}
            (founding engineer). In the gaps, I maintain{" "}
            <a
              href="https://reallyartificial.org"
              target="_blank"
              rel="noreferrer"
              className="text-stone-900 underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-900"
            >
              ReallyArtificial.org
            </a>{" "}
            and write at{" "}
            <a
              href="https://unengineered.substack.com"
              target="_blank"
              rel="noreferrer"
              className="text-stone-900 underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-900"
            >
              Unengineered
            </a>
            .
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            <span
              aria-disabled="true"
              title="Coming soon"
              className="inline-flex cursor-not-allowed items-center gap-2 rounded-full bg-stone-200 px-4 py-2 text-[13px] font-medium text-stone-400 select-none"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Talk to JoshLM
              <span className="text-[10px] uppercase tracking-wider text-stone-400">coming soon</span>
            </span>
            <a
              href="mailto:harsh.joshi.pth@gmail.com"
              className="ml-1 text-[13px] text-stone-500 transition hover:text-stone-900"
            >
              or just say hi →
            </a>
          </div>
        </motion.section>

        <Divider />

        {/* ── currently ─────────────────────────────────────────── */}
        <section>
          <SectionLabel>What I'm up to</SectionLabel>
          <div className="mt-5 grid gap-x-8 gap-y-5 md:grid-cols-2">
            <CurrentlyRow label="Day job" body={
              <>Senior engineer at <ExternalLink href="https://www.coupang.jobs/en/why-coupang/">Coupang</ExternalLink>, on the distributed systems that move millions of orders a day — the software that decides what ships from where, and how it gets to you fastest.</>
            } />
            <CurrentlyRow label="Side project" body={
              <><ExternalLink href="https://reallyartificial.org">ReallyArtificial.org</ExternalLink>, a small open-source org for the unglamorous parts of agent infrastructure. Co-maintained with <ExternalLink href="https://github.com/fridayjoshi">@fridayjoshi</ExternalLink>, an agent I built to help.</>
            } />
            <CurrentlyRow label="Excited about" body={
              <>Applied AI, developer tools, consumer AI apps, and the building blocks that will power tomorrow's AI infrastructure.</>
            } />
            <CurrentlyRow label="Writing" body={
              <><ExternalLink href="https://unengineered.substack.com">Unengineered</ExternalLink>. Short essays. <em>I named the blog Unengineered because the hardest part of engineering was never engineering.</em></>
            } />
          </div>
        </section>

        <Divider />

        {/* ── the story ─────────────────────────────────────────── */}
        <section>
          <SectionLabel>A bit more</SectionLabel>
          <div className="font-lora mt-5 space-y-4 text-[16.5px] leading-[1.7] text-stone-700">
            <p>
              I grew up in Haldwani, near Nainital, far from tech hubs. Open source felt like a doorway into a much larger world: contributing to projects, writing code in public,{" "}
              <a
                href="https://summerofcode.withgoogle.com/archive/2020/organizations/5702344097923072"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-900"
              >
                Google Summer of Code
              </a>
              , open-source communities, and more. <em>I was full-stack before Claude Code made everyone full-stack.</em>
            </p>
            <p>
              I've built 0-to-1 at a 10-person startup (
              <a href="https://plumhq.com" target="_blank" rel="noreferrer" className="underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-900">
                Plum
              </a>
              , where I joined as the founding engineer, and it now backs around 6,000 companies and 600,000 people), scaled systems from one to many, and shipped inside enterprises processing billions of transactions a year (Visa's Authorize.Net) serving at least half the world.
            </p>
            <p>
              I relish building small things that work and contributing to big things that scale; sometimes they're the same thing. I keep ending up in the same place: close to real problems, simple curiosities, and simpler ideas. Sometimes, making complex systems work for the next person.
            </p>
            <p>
              These days I'm excited about applied AI, developer tools, consumer AI apps, and the building blocks that will power tomorrow's AI infrastructure. Most of that work lives at{" "}
              <a href="https://reallyartificial.org" target="_blank" rel="noreferrer" className="underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-900">
                ReallyArtificial.org
              </a>
              .
            </p>
          </div>
        </section>

        <Divider />

        {/* ── experience ────────────────────────────────────────── */}
        <section>
          <SectionLabel>Work</SectionLabel>
          <div className="mt-6 space-y-7">
            {EXPERIENCE.map((e) => (
              <article
                key={e.company + e.year}
                className="grid gap-2 md:grid-cols-[120px_1fr] md:gap-6"
              >
                <div className="text-[12px] uppercase tracking-wider text-stone-400">
                  {e.year}
                </div>
                <div>
                  {e.logo && (
                    <img
                      src={e.logo}
                      alt={`${e.company} logo`}
                      className="mb-2 h-5 w-auto max-w-[110px] object-contain object-left"
                    />
                  )}
                  <h3 className="text-[15px] font-medium text-stone-900">
                    {e.url ? (
                      <a
                        href={e.url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline decoration-stone-300 underline-offset-[3px] hover:decoration-stone-900"
                      >
                        {e.company}
                      </a>
                    ) : (
                      e.company
                    )}
                    <span className="ml-2 font-normal italic text-stone-500">· {e.role}</span>
                    <span className="ml-2 text-[12px] font-normal text-stone-400">
                      · {e.location}
                    </span>
                  </h3>
                  <p className="mt-1.5 text-[14.5px] leading-relaxed text-stone-600">
                    {e.note}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <Divider />

        {/* ── ReallyArtificial ──────────────────────────────────── */}
        <section>
          <div className="flex items-baseline justify-between">
            <SectionLabel>ReallyArtificial.org</SectionLabel>
            <a
              href="https://reallyartificial.org"
              target="_blank"
              rel="noreferrer"
              className="text-[12px] text-stone-500 hover:text-stone-900"
            >
              View org →
            </a>
          </div>
          <p className="mt-3 max-w-xl text-[14px] text-stone-600">
            A small open-source org I started in 2024 for the unglamorous parts of agent infrastructure: testing, memory, gateways, human-in-the-loop. All MIT-licensed. Co-authored with <ExternalLink href="https://github.com/fridayjoshi">@fridayjoshi</ExternalLink>, an autonomous agent that pushes its own commits.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {RA_PROJECTS.map((p) => (
              <a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="group block rounded-lg border border-stone-200 bg-white/60 p-4 transition hover:border-stone-900 hover:bg-white"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <p.icon className="h-[18px] w-[18px] shrink-0 text-stone-400 transition group-hover:text-stone-900" />
                    <h3 className="text-[14.5px] font-medium text-stone-900">{p.name}</h3>
                  </div>
                  <ArrowUpRight className="h-3.5 w-3.5 text-stone-400 transition group-hover:text-stone-900" />
                </div>
                <p className="mt-1 pl-[28.5px] text-[13px] text-stone-600">{p.desc}</p>
              </a>
            ))}
          </div>
        </section>

        <Divider />

        {/* ── open source elsewhere ─────────────────────────────── */}
        <section>
          <SectionLabel>Open source, more broadly</SectionLabel>
          <div className="mt-5 grid gap-x-8 gap-y-5 md:grid-cols-2">
            <CurrentlyRow
              label="Google Summer of Code 2020"
              body={
                <>
                  As part of Google Summer of Code, contributed to{" "}
                  <ExternalLink href="https://submitty.org">Submitty</ExternalLink>
                  , an open-source LMS used by a New York university with around 10,000 students. Built the peer-grading, auto-grading, and grading-quality features, which is probably where I first got hooked on evals.
                </>
              }
            />
            <CurrentlyRow
              label="100LinesOfCode"
              body={
                <>
                  <ExternalLink href="https://github.com/josharsh/100linesofCode">A repo of small, useful tools</ExternalLink>, each under a hundred lines. Picked up about 500 stars somewhere along the way.
                </>
              }
            />
            <CurrentlyRow
              label="npm"
              body={
                <>
                  Around forty packages over the years as{" "}
                  <ExternalLink href="https://npmjs.com/~josharsh">@josharsh</ExternalLink>
                  , including agent-side tools like{" "}
                  <ExternalLink href="https://www.npmjs.com/package/mcp-jest">mcp-jest</ExternalLink>,{" "}
                  <ExternalLink href="https://www.npmjs.com/package/omnimcp">omnimcp</ExternalLink>,{" "}
                  <ExternalLink href="https://www.npmjs.com/package/ensure-json">ensure-json</ExternalLink>, and{" "}
                  <ExternalLink href="https://www.npmjs.com/package/otterly">otterly</ExternalLink>; older bits like{" "}
                  <ExternalLink href="https://www.npmjs.com/package/genai-hooks">genai-hooks</ExternalLink> and{" "}
                  <ExternalLink href="https://www.npmjs.com/package/logi-logger">logi-logger</ExternalLink>.
                </>
              }
            />
            <CurrentlyRow
              label="Community"
              body={
                <>
                  In my college days I led <ExternalLink href="https://upesopen.org">OPEN</ExternalLink>, an open-source community. Spoke at Developer Week 2024. Wrote a course for{" "}
                  <ExternalLink href="https://kodekloud.com/courses/open-source">KodeKloud on open source</ExternalLink>. Was a Microsoft Learn Ambassador back when that mattered.
                </>
              }
            />
          </div>
        </section>

        <Divider />

        {/* ── shipped ───────────────────────────────────────────── */}
        <section>
          <SectionLabel>Shipped</SectionLabel>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {HEROES.map((h) => (
              <a
                key={h.name}
                href={h.url}
                target="_blank"
                rel="noreferrer"
                className="group block rounded-lg border border-stone-300 bg-white p-5 transition hover:border-stone-900"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <h.icon className="h-[21px] w-[21px] shrink-0 text-stone-400 transition group-hover:text-stone-900" />
                    <h4 className="text-[16px] font-semibold text-stone-900">{h.name}</h4>
                  </div>
                  <span className="rounded-full bg-stone-900 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-stone-50">
                    {h.tag}
                  </span>
                </div>
                <p className="mt-2 pl-[31.5px] text-[13.5px] leading-snug text-stone-600">{h.desc}</p>
              </a>
            ))}
          </div>

          <h3 className="mt-8 text-[12.5px] font-medium uppercase tracking-wider text-stone-500">
            iOS Apps
          </h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {APPS.map((a) => (
              <a
                key={a.name}
                href={a.url}
                target="_blank"
                rel="noreferrer"
                className="group block rounded-lg border border-stone-200 bg-white/60 p-4 transition hover:border-stone-900 hover:bg-white"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <a.icon className="h-[18px] w-[18px] shrink-0 text-stone-400 transition group-hover:text-stone-900" />
                    <h4 className="text-[14px] font-medium text-stone-900">{a.name}</h4>
                  </div>
                  <ArrowUpRight className="h-3.5 w-3.5 text-stone-400 transition group-hover:text-stone-900" />
                </div>
                <p className="mt-1 pl-[28.5px] text-[12.5px] leading-snug text-stone-600">{a.meta}</p>
              </a>
            ))}
          </div>

          <h3 className="mt-8 text-[12.5px] font-medium uppercase tracking-wider text-stone-500">
            Other products
          </h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {PRODUCTS.map((p) => (
              <a
                key={p.name}
                href={p.url}
                target={p.url.startsWith("http") ? "_blank" : undefined}
                rel={p.url.startsWith("http") ? "noreferrer" : undefined}
                className="group block rounded-lg border border-stone-200 bg-white/60 p-4 transition hover:border-stone-900 hover:bg-white"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <h4 className="text-[14px] font-medium text-stone-900">{p.name}</h4>
                  <ArrowUpRight className="h-3.5 w-3.5 text-stone-400 transition group-hover:text-stone-900" />
                </div>
                <p className="mt-1 text-[13px] leading-snug text-stone-600">{p.desc}</p>
              </a>
            ))}
          </div>
        </section>

        <Divider />

        {/* ── writing ───────────────────────────────────────────── */}
        <section>
          <SectionLabel>Writing</SectionLabel>
          <p className="mt-3 max-w-xl text-[14.5px] leading-relaxed text-stone-600">
            I write about day-to-day engineering on{" "}
            <ExternalLink href="https://unengineered.substack.com">Unengineered</ExternalLink>.
          </p>
          <ul className="mt-5 space-y-3.5 border-l border-stone-200 pl-5">
            {FEATURED_POSTS.map((p) => (
              <li key={p.url}>
                <a href={p.url} target="_blank" rel="noreferrer" className="group block">
                  <span className="inline-flex items-baseline gap-1 text-[14.5px] font-medium text-stone-900 group-hover:underline decoration-stone-300 underline-offset-[3px]">
                    {p.title}
                    <ArrowUpRight className="h-3 w-3 shrink-0 text-stone-400 transition group-hover:text-stone-900" />
                  </span>
                  <p className="mt-0.5 text-[13px] leading-snug text-stone-500">{p.blurb}</p>
                </a>
              </li>
            ))}
          </ul>
          <a
            href="https://unengineered.substack.com/archive"
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-block pl-5 text-[13px] text-stone-500 hover:text-stone-900"
          >
            More posts on Unengineered →
          </a>
        </section>

        <Divider />

        {/* ── contact / footer ──────────────────────────────────── */}
        <section className="grid gap-6 md:grid-cols-2">
          <div>
            <SectionLabel>Say hi</SectionLabel>
            <div className="mt-4 space-y-2 text-[14.5px]">
              <p>
                <a href="mailto:harsh.joshi.pth@gmail.com" className="inline-flex items-center gap-2 text-stone-900 hover:underline decoration-stone-300 underline-offset-[3px]">
                  <Mail className="h-3.5 w-3.5" /> harsh.joshi.pth@gmail.com
                </a>
              </p>
              <p>
                <a href="https://linkedin.com/in/josharsh" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-stone-900 hover:underline decoration-stone-300 underline-offset-[3px]">
                  <Linkedin className="h-3.5 w-3.5" /> linkedin/in/josharsh
                </a>
              </p>
              <p>
                <a href="https://github.com/josharsh" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-stone-900 hover:underline decoration-stone-300 underline-offset-[3px]">
                  <Github className="h-3.5 w-3.5" /> github.com/josharsh
                </a>
              </p>
            </div>
          </div>
          <div>
            <SectionLabel>Wander around</SectionLabel>
            <div className="mt-4 space-y-2 text-[14.5px]">
              <p>
                <Link to="/ai" className="text-stone-900 hover:underline decoration-stone-300 underline-offset-[3px]">
                  /ai · talk to JoshLM →
                </Link>
              </p>
              <p>
                <Link to="/projects" className="text-stone-900 hover:underline decoration-stone-300 underline-offset-[3px]">
                  /projects · the longer archive →
                </Link>
              </p>
              <p>
                <Link to="/labs" className="text-stone-900 hover:underline decoration-stone-300 underline-offset-[3px]">
                  /labs · short case studies →
                </Link>
              </p>
              <p>
                <Link to="/now" className="text-stone-900 hover:underline decoration-stone-300 underline-offset-[3px]">
                  /now · what's on my desk this week →
                </Link>
              </p>
            </div>
          </div>
        </section>

        <footer className="mt-16 border-t border-stone-200 pt-6 text-[11px] text-stone-400">
          © {new Date().getFullYear()} Harsh Joshi · Bengaluru · Engineering & dark arts
        </footer>
      </div>
    </div>
  );
}

// ──────────────────────────────── tiny bits ────────────────────────────────

function CurrentlyRow({ label, body }: { label: string; body: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-stone-400">
        {label}
      </p>
      <p className="mt-1.5 text-[14.5px] leading-relaxed text-stone-700">{body}</p>
    </div>
  );
}

